/**
 * スキルオートコンプリート入力コンポーネント
 *
 * API から取得したスキルマスターの候補をドロップダウンで表示し、
 * カテゴリ別グルーピング、キーボード操作、バッジ表示をサポートする。
 */

import { useSkillSuggestions } from "hooks/use-skill-suggestions";
import { X } from "lucide-react";
import {
  type FC,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// =====================================
// Types
// =====================================

type SkillAutocompleteProps = {
  /** 選択済みスキル一覧 */
  selectedSkills: string[];
  /** スキル追加コールバック */
  onAddSkill: (skill: string) => void;
  /** スキル削除コールバック */
  onRemoveSkill: (skill: string) => void;
  /** プレースホルダー */
  placeholder?: string;
  /** 無効化 */
  disabled?: boolean;
};

type SkillSuggestion = {
  name: string;
  category: string | null;
  iconUrl: string | null;
};

type GroupedSuggestions = {
  label: string;
  key: string;
  items: SkillSuggestion[];
};

// =====================================
// Constants
// =====================================

const CATEGORY_ORDER: Record<string, { label: string; order: number }> = {
  language: { label: "言語", order: 0 },
  framework: { label: "FW", order: 1 },
  cloud: { label: "クラウド", order: 2 },
  database: { label: "DB", order: 3 },
  infrastructure: { label: "インフラ", order: 4 },
  ai: { label: "AI", order: 5 },
  tool: { label: "ツール", order: 6 },
  os: { label: "OS", order: 7 },
  other: { label: "その他", order: 8 },
};

const UNCATEGORIZED_LABEL = "未分類";
const UNCATEGORIZED_ORDER = 9;
const MAX_DROPDOWN_ITEMS = 50;

// =====================================
// Component
// =====================================

/**
 * スキルオートコンプリート入力コンポーネント
 * @param {SkillAutocompleteProps} props コンポーネントProps
 * @return {JSX.Element} オートコンプリート入力
 */
export const SkillAutocomplete: FC<SkillAutocompleteProps> = ({
  selectedSkills,
  onAddSkill,
  onRemoveSkill,
  placeholder = "スキルを入力",
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { suggestions } = useSkillSuggestions(inputValue);

  // Filter out already-selected skills
  const filteredSuggestions = useMemo(
    () =>
      suggestions
        .filter(s => !selectedSkills.includes(s.name))
        .slice(0, MAX_DROPDOWN_ITEMS),
    [suggestions, selectedSkills],
  );

  // Group by category
  const grouped = useMemo(
    () => groupByCategory(filteredSuggestions),
    [filteredSuggestions],
  );

  // Flat list for keyboard navigation
  const flatItems = useMemo(() => grouped.flatMap(g => g.items), [grouped]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = useCallback(
    (skillName: string) => {
      onAddSkill(skillName);
      setInputValue("");
      setIsOpen(false);
      setHighlightIndex(-1);
      inputRef.current?.focus();
    },
    [onAddSkill],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex(prev =>
          prev < flatItems.length - 1 ? prev + 1 : prev,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex(prev => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (highlightIndex >= 0 && highlightIndex < flatItems.length) {
          handleSelect(flatItems[highlightIndex].name);
        } else if (inputValue.trim()) {
          // Add as free text
          handleSelect(inputValue.trim());
        }
      } else if (e.key === "Escape") {
        setIsOpen(false);
        setHighlightIndex(-1);
      } else if (
        e.key === "Backspace" &&
        !inputValue &&
        selectedSkills.length > 0
      ) {
        onRemoveSkill(selectedSkills[selectedSkills.length - 1]);
      } else if (e.key === "Tab" && isOpen && highlightIndex >= 0) {
        e.preventDefault();
        handleSelect(flatItems[highlightIndex].name);
      }
    },
    [
      flatItems,
      highlightIndex,
      inputValue,
      selectedSkills,
      isOpen,
      handleSelect,
      onRemoveSkill,
    ],
  );

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && dropdownRef.current) {
      const el = dropdownRef.current.querySelector(
        `[data-index="${highlightIndex}"]`,
      );
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex]);

  const showDropdown = isOpen && inputValue.trim().length > 0 && !disabled;

  return (
    <div className="relative">
      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={e => {
          setInputValue(e.target.value);
          setIsOpen(true);
          setHighlightIndex(-1);
        }}
        onFocus={() => {
          if (inputValue.trim()) setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        className="input input-bordered input-sm w-full"
        placeholder={placeholder}
        disabled={disabled}
      />

      {/* Dropdown */}
      {showDropdown && filteredSuggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto bg-base-100 shadow-lg rounded-box border border-base-300"
        >
          {grouped.map(group => (
            <div key={group.key}>
              <div className="text-xs text-base-content/50 px-3 py-1 border-t border-base-200 first:border-t-0 font-semibold bg-base-200/30">
                {group.label}
              </div>
              {group.items.map(item => {
                const globalIndex = flatItems.indexOf(item);
                const isHighlighted = globalIndex === highlightIndex;
                return (
                  <button
                    key={item.name}
                    type="button"
                    data-index={globalIndex}
                    className={`flex items-center w-full px-3 py-1.5 text-sm cursor-pointer ${
                      isHighlighted ? "bg-base-200" : "hover:bg-base-200"
                    }`}
                    onMouseDown={e => {
                      e.preventDefault();
                      handleSelect(item.name);
                    }}
                    onMouseEnter={() => setHighlightIndex(globalIndex)}
                  >
                    {item.iconUrl ? (
                      <img
                        src={item.iconUrl}
                        alt=""
                        className="w-4 h-4 mr-2 object-contain"
                      />
                    ) : (
                      <span className="w-4 h-4 mr-2" />
                    )}
                    <HighlightedText text={item.name} query={inputValue} />
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Selected badges */}
      {selectedSkills.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedSkills.map(skill => (
            <span key={skill} className="badge badge-primary gap-1">
              {skill}
              <button
                type="button"
                onClick={() => onRemoveSkill(skill)}
                disabled={disabled}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// =====================================
// Helpers
// =====================================

/**
 * サジェスト結果をカテゴリ別にグルーピング
 * @param {SkillSuggestion[]} items サジェストアイテム
 * @return {GroupedSuggestions[]} グルーピング済みリスト
 */
function groupByCategory(items: SkillSuggestion[]): GroupedSuggestions[] {
  const groups = new Map<string, SkillSuggestion[]>();

  for (const item of items) {
    const key = item.category || "_uncategorized";
    const list = groups.get(key) || [];
    list.push(item);
    groups.set(key, list);
  }

  return Array.from(groups.entries())
    .map(([key, groupItems]) => ({
      key,
      label:
        key === "_uncategorized"
          ? UNCATEGORIZED_LABEL
          : (CATEGORY_ORDER[key]?.label ?? key),
      order:
        key === "_uncategorized"
          ? UNCATEGORIZED_ORDER
          : (CATEGORY_ORDER[key]?.order ?? 8),
      items: groupItems,
    }))
    .sort((a, b) => a.order - b.order);
}

/**
 * 入力文字列に一致する部分を太字でハイライト表示
 * @param {object} props テキストとクエリ
 * @param {string} props.text 表示テキスト
 * @param {string} props.query 検索クエリ
 * @return {JSX.Element} ハイライト済みテキスト
 */
const HighlightedText: FC<{ text: string; query: string }> = ({
  text,
  query,
}) => {
  const q = query.trim().toLowerCase();
  if (!q || !text.toLowerCase().startsWith(q)) {
    return <span>{text}</span>;
  }
  const matchLen = q.length;
  return (
    <span>
      <span className="font-bold">{text.slice(0, matchLen)}</span>
      {text.slice(matchLen)}
    </span>
  );
};
